import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';

import { OptionalStartupable } from '@/services/base/Startupable';
import {
  isStartupableStarted,
  registerStartupable,
} from '@/services/base/StartupHelper';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';

import { getCardUrl, getManageCardUrl } from './helpers/WorldpayApi';

class WorldpayJCBService implements OptionalStartupable {
  public readonly name = 'WorldpayJCBService';
  public readonly moduleId = PaymentModuleId.WorldpayJCB;

  private _cachedUrls: { [key: string]: string } = {};

  constructor() {
    registerStartupable(this, [PaymentStore]);
  }

  public shouldStart(): boolean {
    const isAllowed = PaymentStore.allowedPaymentModules.includes(
      this.moduleId,
    );

    const hasInstruments =
      PaymentStore.instruments.find((i) =>
        [this.moduleId].includes(i.moduleId),
      ) !== undefined;

    return isAllowed || hasInstruments;
  }

  public startup(): Promise<void> {
    this._cachedUrls = {};
    return Promise.resolve();
  }

  public async getRedirectUrl(
    locale: SupportedLanguage,
    accessToken: string,
    paymentInstrumentId?: string,
  ): Promise<string> {
    const telemetryConsent = isStartupableStarted(TelemetryService);
    const paymentIntentId = PaymentStore.paymentIntentId;
    const redirectUrl = this.getCachedUrl(
      paymentIntentId,
      locale,
      telemetryConsent,
      paymentInstrumentId,
    );

    if (redirectUrl) {
      return Promise.resolve(redirectUrl);
    }

    const url = await getCardUrl(
      paymentIntentId,
      accessToken,
      paymentInstrumentId,
      locale,
      telemetryConsent,
      this.moduleId,
    );

    const key = this.computeCacheKeyFromParams(
      paymentIntentId,
      locale,
      telemetryConsent,
      paymentInstrumentId,
    );
    this.saveUrlInCache(key, url);

    return url;
  }

  public async getManageRedirectUrl(
    legalEntityNumber: number,
    customerNumber: string,
    accessToken: string,
    locale: SupportedLanguage,
  ): Promise<string> {
    const telemetryConsent = isStartupableStarted(TelemetryService);

    const redirectUrl = this.getCachedUrl(
      '',
      locale,
      telemetryConsent,
      undefined,
    );

    if (redirectUrl) {
      return Promise.resolve(redirectUrl);
    }

    const url = await getManageCardUrl(
      legalEntityNumber,
      customerNumber,
      accessToken,
      this.moduleId,
      locale,
      telemetryConsent,
    );

    const key = this.computeCacheKeyFromParams(
      '',
      locale,
      telemetryConsent,
      undefined,
    );
    this.saveUrlInCache(key, url);

    return url;
  }

  private getCachedUrl(
    paymentIntentId: string,
    locale: SupportedLanguage,
    telemetryConsent: boolean,
    paymentInstrumentId?: string,
  ): string | undefined {
    const key = this.computeCacheKeyFromParams(
      paymentIntentId,
      locale,
      telemetryConsent,
      paymentInstrumentId,
    );
    return this._cachedUrls[key];
  }

  private computeCacheKeyFromParams(
    paymentIntentId: string,
    locale: SupportedLanguage,
    telemetryConsent: boolean,
    paymentInstrumentId?: string,
  ): string {
    return `${paymentIntentId}-${locale}-${telemetryConsent}-${
      paymentInstrumentId ?? ''
    }`;
  }

  private saveUrlInCache(key: string, value: string): void {
    this._cachedUrls[key] = value;
  }
}

export default new WorldpayJCBService();
